import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FiBell, FiChevronDown, FiX } from "react-icons/fi";
import { formatDistanceToNow, parseISO } from "date-fns";

interface Notification {
  type: string;
  title: string;
  entityId: string;
  occurredAt?: string;
  timeAgo: string;
  time: string;
  isRead: boolean;
}

const getToken = () => {
  const auth = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token as string | undefined;
};

const authHeaders = (token?: string) =>
  token ? { Authorization: `Bearer ${token}` } : undefined;

export default function WalkInAlertsPill() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  
  const modalRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/notifications?filter=${filter}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        const notifs = res.data.data.notifications || [];
        setNotifications(notifs);
        
        // Only update unread count if we're fetching "all", to keep the pill accurate
        // or just count the unread from the response
        if (res.data.data.count !== undefined) {
          setUnreadCount(res.data.data.count);
        } else {
          setUnreadCount(notifs.filter((n: Notification) => !n.isRead).length);
        }
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={modalRef}>
      {/* Alerts Pill Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-100 shadow-sm text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors"
      >
        <div className="relative">
          <FiBell className="w-4 h-4 text-gray-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </div>
        <span>Alerts</span>
      </button>

      {/* Notifications Dropdown Modal */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[380px] bg-white rounded-xl shadow-xl border border-gray-100 z-50 flex flex-col max-h-[500px]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <h3 className="font-bold text-gray-900">Notifications</h3>
            
            <div className="flex items-center gap-3">
              {/* Filter Dropdown */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="appearance-none pr-7 pl-2 py-1 bg-gray-50 border border-gray-100 rounded text-xs text-gray-600 font-medium focus:outline-none cursor-pointer"
                >
                  <option value="all">All</option>
                  <option value="today">Today</option>
                  <option value="this_week">This Week</option>
                  <option value="last_week">Last Week</option>
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <FiChevronDown className="w-3 h-3" />
                </div>
              </div>
              
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#008060]"></div>
              </div>
            ) : notifications.length > 0 ? (
              <div className="flex flex-col gap-1">
                {notifications.map((notif, idx) => (
                  <div 
                    key={notif.entityId + idx} 
                    className={`flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors ${!notif.isRead ? 'bg-[#f0fbf6]' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#EAF5EF] text-[#008060] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FiBell className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium line-clamp-2">
                        {notif.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 capitalize">
                        {notif.timeAgo !== "NaN day ago" ? notif.timeAgo : notif.time}
                        {notif.occurredAt && notif.timeAgo === "NaN day ago" && (
                          formatDistanceToNow(parseISO(notif.occurredAt), { addSuffix: true })
                        )}
                      </p>
                    </div>
                    {!notif.isRead && (
                      <div className="w-2 h-2 rounded-full bg-red-500 mt-2 flex-shrink-0"></div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <FiBell className="w-8 h-8 mb-2 opacity-20" />
                <p className="text-sm">No notifications found</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-3 border-t border-gray-50 text-center">
            <button className="text-xs font-semibold text-[#008060] hover:text-[#006b50]">
              Mark all as read
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
